// src/App.jsx
import React, { Suspense, useEffect } from 'react';
import { HashRouter, Route, Routes, Navigate } from 'react-router-dom';
import { CSpinner } from '@coreui/react';
import './scss/style.scss';
import UserRatingPage     from './views/userReport/UserRatingPage';
import ReviewerDashboard  from './views/review/ReviewerDashboard';
import {
  initChatUnreadListener,
  destroyChatUnreadListener,
} from './components/chatUnreadListener';
import './components/debugChatUnread';
// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));

// Pages - Login and Auth
const Login          = React.lazy(() => import('./views/pages/login/Login'));
const Register       = React.lazy(() => import('./views/pages/register/Register'));
const Page404        = React.lazy(() => import('./views/pages/page404/Page404'));
const Page500        = React.lazy(() => import('./views/pages/page500/Page500'));
const ForgotPassword = React.lazy(() => import('./views/pages/login/ForgotPassword'));
const OTPPage        = React.lazy(() => import('./views/pages/login/OTPPage'));
const SetNewPassword = React.lazy(() => import('./views/pages/login/SetNewPassword'));

// Main Application Pages
const DashboardContent       = React.lazy(() => import('./components/pages/DashboardContent'));
const TasksContent           = React.lazy(() => import('./components/pages/TasksContent'));
const TimerContent           = React.lazy(() => import('./components/pages/TimerContent'));
const LeavesContent          = React.lazy(() => import('./components/pages/LeavesContent'));
const UnifiedAttendance      = React.lazy(() => import('./components/pages/UnifiedAttendance'));
const AttendanceSummary      = React.lazy(() => import('./components/pages/AttendanceSummary'));
const AttendanceTimeTracking = React.lazy(() => import('./components/pages/AttendanceTimeTracking'));
const HolidaysContent        = React.lazy(() => import('./components/pages/HolidaysContent'));
const TaskTimer              = React.lazy(() => import('./components/pages/TaskTimer'));
const ProjectChat            = React.lazy(() => import('./components/pages/ProjectChat'));
const TaskDetail             = React.lazy(() => import('./components/pages/TaskDetail'));
const TaskFilter             = React.lazy(() => import('./components/pages/TaskFilter'));
const TaskTable              = React.lazy(() => import('./components/pages/TaskTable'));
const MyTasksV2              = React.lazy(() => import('./components/pages/MyTasksV2'));

// ─────────────────────────────────────────────────────────────
// PROTECTED ROUTE
// ─────────────────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
};

// ─────────────────────────────────────────────────────────────
// RESOLVE USER ID
// Tries both storage keys the app uses
// ─────────────────────────────────────────────────────────────
function resolveUserId() {
  try {
    const tokenStr = localStorage.getItem('token');
    if (tokenStr) {
      const parsed = JSON.parse(tokenStr);
      const id = parsed?.userId || parsed?.employeeId || parsed?.id || null;
      if (id) return id;
    }
  } catch { /* ignore */ }

  try {
    const raw = localStorage.getItem('sessionData');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.userId || null;
    }
  } catch { /* ignore */ }

  return null;
}

// ─────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────
const App = () => {

  useEffect(() => {
    // ── INIT GLOBAL CHAT UNREAD LISTENER ─────────────────────
    // Called once on app mount (after login page sets localStorage).
    // The listener calls webSocketService.acquire() internally so the
    // singleton connection persists across ALL page navigations.
    // destroyChatUnreadListener() is called on logout via AppHeader.
    const userId = resolveUserId();

    if (userId) {
      initChatUnreadListener(userId);
    } else {
      console.warn('App: no userId found — WebSocket not initialised');
    }

    // ── CLEANUP on tab close / hard refresh ──────────────────
    // Note: we do NOT return destroyChatUnreadListener here because
    // the React StrictMode double-invoke would immediately destroy
    // what we just created. Instead the listener self-manages its
    // lifecycle via the reference counter in WebSocketService.
    // Explicit teardown only happens on logout (see AppHeader).
  }, []); // empty deps → runs once after first render

  return (
    <HashRouter>
      <Suspense
        fallback={
          <div className="pt-3 text-center">
            <CSpinner color="primary" variant="grow" />
          </div>
        }
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/login"          element={<Login />} />
          <Route path="/forgotPassword" element={<ForgotPassword />} />
          <Route path="/otpPage"        element={<OTPPage />} />
          <Route path="/setNewPassword" element={<SetNewPassword />} />
          <Route path="/register"       element={<Register />} />
          <Route path="/404"            element={<Page404 />} />
          <Route path="/500"            element={<Page500 />} />

          {/* Protected Routes */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <DefaultLayout />
              </ProtectedRoute>
            }
          >
            <Route index                               element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"                   element={<DashboardContent />} />
            <Route path="tasks"                       element={<TasksContent />} />
            <Route path="timer"                       element={<TimerContent />} />
            <Route path="leaves"                      element={<LeavesContent />} />
            <Route path="attendance"                  element={<UnifiedAttendance />} />
            <Route path="attendance/summary"          element={<AttendanceSummary />} />
            <Route path="attendance/tracking"         element={<AttendanceTimeTracking />} />
            <Route path="holidays"                    element={<HolidaysContent />} />
            <Route path="task-timer"                  element={<TaskTimer />} />
            <Route path="project-chat"                element={<ProjectChat />} />
            <Route path="task/:id"                    element={<TaskDetail />} />
            <Route path="task-filter"                 element={<TaskFilter />} />
            <Route path="task-table"                  element={<TaskTable />} />
            <Route path="userRatingDetailView"        element={<UserRatingPage />} />
            <Route path="review/reviewerDashboard"   element={<ReviewerDashboard />} />
            <Route path="mytasks"                     element={<MyTasksV2 />} />
          </Route>
        </Routes>
      </Suspense>
    </HashRouter>
  );
};

export default App;